/** Hooks React Query del módulo productos (skill modulo-canonico §7). */
import type { CategoriaDTO, DescuentoProgramadoDTO, DescuentoProgramadoInput, HistoricoPrecioDTO, ListaPrecioDTO, MinimoAlmacenInput, MinimoInput, PreciosInput, PresentacionDTO, PresentacionEditarInput, PresentacionInput, ProductoCrearInput, ProductoDetalleDTO, ProductoEditarInput, ProductoResumenDTO } from '@mg/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, patch, post, put } from '@/lib/api.ts';

export interface FiltrosProductos {
  q?: string | undefined;
  categoria_id?: string | undefined;
  activo?: 'true' | 'false' | 'todos' | undefined;
  lista_id?: string | undefined;
  bajo_minimo?: 'true' | undefined;
  pagina?: number | undefined;
  limite?: number | undefined;
}
export interface PaginaProductos {
  datos: ProductoResumenDTO[];
  total: number;
  pagina: number;
  limite: number;
}

export const clavesProductos = {
  lista: (f: FiltrosProductos) => ['productos', 'lista', f] as const,
  detalle: (id: string) => ['productos', 'detalle', id] as const,
  historico: (id: string) => ['productos', 'historico', id] as const,
  listas: ['productos', 'listas-precio'] as const,
  categorias: ['productos', 'categorias'] as const,
};

export const useProductos = (f: FiltrosProductos) => useQuery({ queryKey: clavesProductos.lista(f), queryFn: () => get<PaginaProductos>('/productos', f as Record<string, string | number | undefined>), placeholderData: (prev) => prev });
export const useProducto = (id: string | null) => useQuery({ queryKey: clavesProductos.detalle(id ?? ''), queryFn: () => get<ProductoDetalleDTO>(`/productos/${id}`), enabled: !!id });
export const useHistoricoPrecios = (id: string | null, habilitado: boolean) => useQuery({ queryKey: clavesProductos.historico(id ?? ''), queryFn: () => get<HistoricoPrecioDTO[]>(`/productos/${id}/historico-precios`), enabled: !!id && habilitado });
export const useListasPrecio = () => useQuery({ queryKey: clavesProductos.listas, queryFn: () => get<ListaPrecioDTO[]>('/productos/listas-precio'), staleTime: 5 * 60_000 });
export const useCategorias = () => useQuery({ queryKey: clavesProductos.categorias, queryFn: () => get<CategoriaDTO[]>('/productos/categorias'), staleTime: 5 * 60_000 });

function useInvalidar() {
  const qc = useQueryClient();
  return (id?: string) => Promise.all([qc.invalidateQueries({ queryKey: ['productos', 'lista'] }), id ? qc.invalidateQueries({ queryKey: clavesProductos.detalle(id) }) : Promise.resolve(), id ? qc.invalidateQueries({ queryKey: clavesProductos.historico(id) }) : Promise.resolve()]);
}

export function useCrearProducto() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: (d: ProductoCrearInput) => post<ProductoDetalleDTO>('/productos', d), onSuccess: () => inv() });
}
export function useEditarProducto(id: string) {
  const inv = useInvalidar();
  return useMutation({ mutationFn: (d: ProductoEditarInput) => patch<ProductoDetalleDTO>(`/productos/${id}`, d), onSuccess: () => inv(id) });
}
export function useCrearPresentacion(id: string) {
  const inv = useInvalidar();
  return useMutation({ mutationFn: (d: PresentacionInput) => post<PresentacionDTO>(`/productos/${id}/presentaciones`, d), onSuccess: () => inv(id) });
}
export function useEditarPresentacion(id: string) {
  const inv = useInvalidar();
  return useMutation({ mutationFn: ({ pid, datos }: { pid: string; datos: PresentacionEditarInput }) => patch<PresentacionDTO>(`/productos/${id}/presentaciones/${pid}`, datos), onSuccess: () => inv(id) });
}
export function useActualizarPrecios(id: string) {
  const inv = useInvalidar();
  return useMutation({ mutationFn: (d: PreciosInput) => put<{ producto: ProductoDetalleDTO; advertencias: string[] }>(`/productos/${id}/precios`, d), onSuccess: () => inv(id) });
}
export function useFijarMinimo(id: string) {
  const inv = useInvalidar();
  return useMutation({ mutationFn: (d: MinimoInput) => put<ProductoDetalleDTO>(`/productos/${id}/minimos`, d), onSuccess: () => inv(id) });
}
/** Mínimo de un depósito o tienda concretos (la alerta es por ubicación). */
export function useFijarMinimoAlmacen(id: string) {
  const inv = useInvalidar();
  return useMutation({ mutationFn: (d: MinimoAlmacenInput) => put<ProductoDetalleDTO>(`/productos/${id}/minimos-almacen`, d), onSuccess: () => inv(id) });
}

// Descuentos programados: cambian el precio efectivo, así que refrescan también ventas y cotizaciones
export const useDescuentos = () => useQuery({ queryKey: ['productos', 'descuentos'], queryFn: () => get<DescuentoProgramadoDTO[]>('/productos/descuentos') });
function useInvalidarDescuentos() {
  const qc = useQueryClient();
  return () => Promise.all([qc.invalidateQueries({ queryKey: ['productos', 'descuentos'] }), qc.invalidateQueries({ queryKey: ['ventas'] }), qc.invalidateQueries({ queryKey: ['cotizaciones'] })]);
}
export function useCrearDescuento() {
  const inv = useInvalidarDescuentos();
  return useMutation({ mutationFn: (d: DescuentoProgramadoInput) => post<DescuentoProgramadoDTO>('/productos/descuentos', d), onSuccess: () => inv() });
}
export function useActivarDescuento() {
  const inv = useInvalidarDescuentos();
  return useMutation({ mutationFn: ({ id, activo }: { id: string; activo: boolean }) => post<DescuentoProgramadoDTO>(`/productos/descuentos/${id}/${activo ? 'activar' : 'desactivar'}`), onSuccess: () => inv() });
}
